import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const AudioLooper = forwardRef(function AudioLooper(
  {
    url,
    volume = 1,
    fadeIn = 0.25,
    fadeOut = 0.25,
    loopStart = 0.15,
    loopEndTrim = 0.15, // loopEnd = duration - loopEndTrim
    autoplay = false,
  },
  ref
) {
  const audioContextRef = useRef(null);
  const bufferRef = useRef(null);

  const masterGainRef = useRef(null);
  const sourceRef = useRef(null);

  const loadingRef = useRef(false);
  const pendingPlayRef = useRef(false);

  const ensureContext = () => {
    if (!audioContextRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AC();
      const ctx = audioContextRef.current;

      const master = ctx.createGain();
      master.gain.value = 0;
      master.connect(ctx.destination);
      masterGainRef.current = master;
    }
    return audioContextRef.current;
  };

  const loadBuffer = async () => {
    if (bufferRef.current || loadingRef.current) return bufferRef.current;
    loadingRef.current = true;
    try {
      const ctx = ensureContext();
      const res = await fetch(url);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      bufferRef.current = buf;
      return buf;
    } finally {
      loadingRef.current = false;
    }
  };

  const stopInternalNow = () => {
    const src = sourceRef.current;
    if (!src) return;
    try {
      src.onended = null;
      src.stop();
    } catch {}
    try {
      src.disconnect();
    } catch {}
    sourceRef.current = null;
  };

  const getGainNow = (gainParam, t) => {
    // evita saltos si había ramps
    try {
      return gainParam.getValueAtTime ? gainParam.getValueAtTime(t) : gainParam.value;
    } catch {
      return gainParam.value;
    }
  };

  const play = async () => {
    pendingPlayRef.current = true;

    const ctx = ensureContext();
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {}
    }

    const buf = await loadBuffer();
    if (!pendingPlayRef.current || !buf) return;

    const master = masterGainRef.current;
    if (!master) return;

    // Si ya está sonando, no recrear
    if (sourceRef.current) return;

    const safeLoopStart = Math.max(0, loopStart);
    const safeLoopEnd = Math.max(
      safeLoopStart + 0.01,
      buf.duration - Math.max(0, loopEndTrim)
    );

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.loopStart = safeLoopStart;
    src.loopEnd = safeLoopEnd;
    src.connect(master);

    const now = ctx.currentTime + 0.02;

    // Master fade-in limpio
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(volume, now + Math.max(0.001, fadeIn));

    src.start(now, safeLoopStart);
    sourceRef.current = src;

    src.onended = () => {
      if (sourceRef.current === src) sourceRef.current = null;
      try {
        src.disconnect();
      } catch {}
    };
  };

  const stop = async () => {
    pendingPlayRef.current = false;

    const ctx = audioContextRef.current;
    const master = masterGainRef.current;
    const src = sourceRef.current;
    if (!ctx || !master || !src) return;

    const now = ctx.currentTime;

    // Fade-out desde el valor real actual
    const current = getGainNow(master.gain, now);

    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(Math.max(0.0001, current), now);
    master.gain.linearRampToValueAtTime(0.0001, now + Math.max(0.001, fadeOut));

    try {
      src.stop(now + Math.max(0.001, fadeOut) + 0.02);
    } catch {}

    src.onended = () => {
      try {
        src.disconnect();
      } catch {}
      if (sourceRef.current === src) sourceRef.current = null;

      // Dejar master listo para el próximo play
      try {
        const t = audioContextRef.current?.currentTime ?? 0;
        masterGainRef.current?.gain?.cancelScheduledValues(t);
        masterGainRef.current?.gain?.setValueAtTime(0, t);
      } catch {}
    };
  };

  const setVolume = (v) => {
    const ctx = audioContextRef.current;
    const master = masterGainRef.current;
    if (!ctx || !master) return;

    const now = ctx.currentTime;
    const current = getGainNow(master.gain, now);

    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(current, now);
    master.gain.linearRampToValueAtTime(v, now + 0.05);
  };

  useImperativeHandle(ref, () => ({
    play,
    stop,
    setVolume,
    isPlaying: () => !!sourceRef.current,
    isLoaded: () => !!bufferRef.current,
    preload: () => loadBuffer(),
  }));

  useEffect(() => {
    loadBuffer().catch(() => {});

    if (autoplay) {
      play().catch(() => {});
    }

    return () => {
      pendingPlayRef.current = false;

      try {
        stopInternalNow();
      } catch {}

      try {
        masterGainRef.current?.disconnect();
      } catch {}
      try {
        audioContextRef.current?.close();
      } catch {}

      masterGainRef.current = null;
      audioContextRef.current = null;
      bufferRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return null;
});

export default AudioLooper;
